import { useEffect } from 'react';
import { classnames } from 'tailwindcss-classnames';
import { AuthenticatedRecentNetworkAnnouncementsProps } from './RecentNetworkAnnouncementsTypes';
import { Configure, Hits, SortBy, useSortBy } from 'react-instantsearch-hooks-web';
import { DEFAULT_SEARCH_INDEX } from 'lib/util/algolia';
import { FilterBuilder } from 'lib/util/filter-builder';
import { distinctAnnouncementAudiencesForCenterTypes } from 'lib/util/audiences';
import { HitProps } from 'components/helpers/AlgoliaSearch/Hits/_types';
import Button from 'components/helpers/Button';
import fnNoOp from 'lib/util/no-op';

export const buildAnnouncementFilterString = (centerTypes: string[]): string => {
  const filterBuilder = new FilterBuilder();
  distinctAnnouncementAudiencesForCenterTypes(centerTypes).forEach((centerType: string) =>
    filterBuilder.appendFilter(`audience: "${centerType}"`)
  );
  const centerFilter = filterBuilder.toString();
  return centerFilter.length > 0
    ? `type: announcement AND (${centerFilter})`
    : `type: announcement`;
};

const hitComponent = ({ hit }: HitProps): React.JSX.Element => {
  const cssCard = classnames('py-[3px]', 'md:py-[9px]', 'px-[12px]', 'rounded-lg', 'bg-white');

  const renderAnnouncementDate = (result: string | number | Date) => {
    const dateString = new Date(result);
    const options: {} = { year: 'numeric', month: 'long', day: 'numeric' };
    return (
      <div className={classnames('mb-2', 'text-sm', 'leading-2')}>
        {dateString.toLocaleDateString(undefined, options)}
      </div>
    );
  };

  return (
    <div className={classnames('relative')}>
      <div className={cssCard}>
        {hit.publishedDate && renderAnnouncementDate(hit.publishedDate)}
        <h3
          className={classnames(
            'text-body',
            'leading-4',
            'text-blue',
            'line-clamp-3',
            'font-regular'
          )}
        >
          <Button
            linkField={{ value: { href: hit.url } }}
            variant="link"
            suppressLinkText="true"
            className={classnames('text-body', 'font-regular')}
          >
            {hit.pageTitle}
          </Button>
        </h3>
      </div>
    </div>
  );
};

const Authenticated = ({
  fields,
  session,
}: AuthenticatedRecentNetworkAnnouncementsProps): React.JSX.Element => {
  // use dependencies
  const sortItems = [
    { label: 'sortDate', value: `${DEFAULT_SEARCH_INDEX}_date_desc` },
    { label: 'default', value: DEFAULT_SEARCH_INDEX },
  ];
  const { refine, canRefine } = useSortBy({
    items: sortItems,
  });

  // Set sort index on mount
  useEffect(() => {
    const isBrowser = typeof window !== 'undefined'; // Check if window is defined
    if (isBrowser && canRefine) {
      const delay = 100;
      const timer = setTimeout(() => {
        refine(`${DEFAULT_SEARCH_INDEX}_date_desc`);
      }, delay);
      return () => clearTimeout(timer);
    }
    return fnNoOp;
  }, [refine, canRefine]);

  // Technically we should never get here with empty fields, but good to be prepared
  if (!fields) {
    return <></>;
  }
  const activeFilter = buildAnnouncementFilterString(session?.center_types || []);
  const cssGrid = 'theme-white-nrc';
  return (
    <div>
      <Configure
        analytics={false}
        filters={activeFilter}
        hitsPerPage={3}
        index={`${DEFAULT_SEARCH_INDEX}_date_desc`}
      />
      <SortBy classNames={{ root: classnames('hidden') }} items={sortItems} />
      <div className={cssGrid}>
        <Hits hitComponent={hitComponent} />
      </div>
    </div>
  );
};

export default Authenticated;
